import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Card,
  Form,
  Input,
  Select,
  Button,
  Alert,
  message,
  Skeleton,
  Divider,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons";
import {
  assignQuestionnaire,
  getCompanyEmployees,
} from "../repository/data.repository";
import { EmployeeUser } from "../repository/data.default";
import { useTokenInfo } from "../hooks/useTokenInfo";
import { getProblemMessage } from "../utils/http";

interface Employee {
  readonly uid: string;
  readonly label: string;
}

// PD-02 §6.16.1 — Assign Assessment / Bundle. Picks one or more employees of
// the signed-in user's company and posts a QuestionnaireAnswer draft for the
// chosen questionnaire so it appears in their My Tasks → Assessments list.
function AssignOceanIvPage() {
  const { claims } = useTokenInfo();
  const companyUid = claims?.usrCmpUid;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCompanyEmployees({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 500,
    })
      .then((data) => {
        if (cancelled) return;
        const rows = (data.data ?? []).map((e: EmployeeUser) => ({
          uid: e.uid,
          label:
            [e.firstName, e.lastName].filter(Boolean).join(" ") ||
            e.userName ||
            e.uid,
        }));
        setEmployees(rows);
      })
      .catch(() => setEmployees([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(values: {
    questionnaireUid: string;
    userUids: string[];
  }) {
    if (!values.userUids?.length) return;
    setSubmitting(true);
    const results = await Promise.allSettled(
      values.userUids.map((uid) =>
        assignQuestionnaire({
          userUid: uid,
          questionnaireUid: values.questionnaireUid,
          companyUid,
        }),
      ),
    );
    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;
    if (failed === 0) {
      message.success(`Assigned to ${succeeded} employee(s).`);
    } else if (succeeded === 0) {
      const first = results.find((r) => r.status === "rejected") as
        | PromiseRejectedResult
        | undefined;
      message.error(
        getProblemMessage(first?.reason) || "Failed to assign assessment.",
      );
    } else {
      message.warning(
        `Assigned to ${succeeded}; ${failed} failed. Check if they already have a draft.`,
      );
    }
    form.resetFields(["userUids"]);
    setSubmitting(false);
  }

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-6">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
          Assign Assessment
        </Typography.Title>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Pick a questionnaire UID (shared with you by Admin) and the employees who should take it. Each gets a draft they can continue from their My Tasks tab."
      />

      <Card className="emp-card">
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="questionnaireUid"
            label="Questionnaire UID"
            rules={[
              { required: true, message: "Paste the questionnaire UID" },
            ]}
          >
            <Input placeholder="e.g. 7b1f1a0c-… (ask Admin for the UID)" />
          </Form.Item>
          <Divider />
          <Form.Item
            name="userUids"
            label="Employees"
            rules={[
              {
                required: true,
                message: "Pick at least one employee",
                type: "array",
                min: 1,
              },
            ]}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <Select
                mode="multiple"
                showSearch
                placeholder="Pick employees"
                optionFilterProp="label"
                options={employees.map((e) => ({ value: e.uid, label: e.label }))}
                maxTagCount="responsive"
              />
            )}
          </Form.Item>
          <Row className="justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Assign
            </Button>
          </Row>
        </Form>
      </Card>
    </Col>
  );
}

export default AssignOceanIvPage;
